import { api } from "./dataService";

/**
 * Download assignment import template
 * @param {string} kundeIdOv - Customer ID
 * @param {Object} options - Template options
 * @param {boolean} options.prepopulated - Whether to include current assignments
 * @param {boolean} options.include_catalogs - Whether to include catalog sheets
 * @param {string} options.format - File format (xlsx)
 */
export const downloadAssignmentTemplate = async (kundeIdOv, options = {}) => {
  const params = new URLSearchParams();

  if (options.prepopulated) {
    params.append("prepopulated", "true");
  }

  if (options.include_catalogs !== undefined) {
    params.append(
      "include_catalogs",
      options.include_catalogs ? "true" : "false"
    );
  }

  if (options.format) {
    params.append("format", options.format);
  }

  const url = `data/customers/${kundeIdOv}/imports/assignments/template${params.toString() ? "?" + params.toString() : ""}`;

  try {
    const format = (options.format || "xlsx").toLowerCase();
    const acceptHeader =
      format === "csv"
        ? "text/csv"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    const response = await api.get(url, {
      responseType: "blob",
      headers: {
        Accept: acceptHeader,
      },
    });

    // Create download link
    const blobType = acceptHeader;
    const blob = new Blob([response.data], { type: blobType });

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;

    // Generate filename with current date
    const date = new Date().toISOString().split("T")[0];
    const ext = format === "csv" ? "csv" : "xlsx";
    link.download = `${date}_benutzer_vertragszuordnung_${kundeIdOv}.${ext}`;

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    window.URL.revokeObjectURL(downloadUrl);

    return response;
  } catch (error) {
    console.error("Assignment template download failed:", error);
    throw error;
  }
};

/**
 * Upload an assignment import file (CSV or XLSX)
 * @param {string} kundeIdOv
 * @param {File} file
 * @returns {Promise<{import_job_id:number,status:string}>}
 */
export const uploadAssignmentFile = async (kundeIdOv, file) => {
  const url = `data/customers/${kundeIdOv}/imports/assignments/upload`;

  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await api.post(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    console.error("Assignment import upload failed:", error);
    throw error;
  }
};

/**
 * Request a preview (validation + change set) for an uploaded import job
 * @param {string} kundeIdOv
 * @param {number} importJobId
 * @returns {Promise<object>} preview payload
 */
export const previewAssignmentImport = async (kundeIdOv, importJobId) => {
  const url = `data/customers/${kundeIdOv}/imports/assignments/preview`;
  try {
    const response = await api.post(url, { import_job_id: importJobId });
    return response.data;
  } catch (error) {
    console.error("Assignment import preview failed:", error);
    throw error;
  }
};

/**
 * Apply an import job
 * @param {string} kundeIdOv
 * @param {number} importJobId
 * @returns {Promise<object>} apply response
 */
export const applyAssignmentImport = async (kundeIdOv, importJobId) => {
  const url = `data/customers/${kundeIdOv}/imports/assignments/apply`;
  try {
    const response = await api.post(url, { import_job_id: importJobId });
    return response.data;
  } catch (error) {
    console.error("Assignment import apply failed:", error);
    throw error;
  }
};

export const listAssignmentImportJobs = async (kundeIdOv, params = {}) => {
  const query = new URLSearchParams();

  if (params.page) {
    query.append("page", String(params.page));
  }

  if (params.per_page) {
    query.append("per_page", String(params.per_page));
  }

  if (params.status) {
    query.append("status", params.status);
  }

  const url = `data/customers/${kundeIdOv}/imports/assignments/jobs${query.toString() ? "?" + query.toString() : ""}`;

  try {
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error("Assignment import jobs fetch failed:", error);
    throw error;
  }
};

export const previewAssignmentRollback = async (kundeIdOv, importJobId) => {
  const url = `data/customers/${kundeIdOv}/imports/assignments/rollback/preview`;

  try {
    const response = await api.post(url, {
      import_job_id: importJobId,
    });
    return response.data;
  } catch (error) {
    console.error("Assignment rollback preview failed:", error);
    throw error;
  }
};

export const applyAssignmentRollback = async (
  kundeIdOv,
  rollbackJobId,
  confirm = true
) => {
  const url = `data/customers/${kundeIdOv}/imports/assignments/rollback/apply`;

  try {
    const response = await api.post(url, {
      rollback_job_id: rollbackJobId,
      confirm,
    });
    return response.data;
  } catch (error) {
    console.error("Assignment rollback apply failed:", error);
    throw error;
  }
};

/**
 * Get status of an import job
 * @param {string} kundeIdOv
 * @param {number} importJobId
 * @returns {Promise<object>} status payload
 */
export const getAssignmentImportJobStatus = async (
  kundeIdOv,
  importJobId,
  options = {}
) => {
  const params = new URLSearchParams();
  if (options.debug_delay_ms && Number.isFinite(options.debug_delay_ms)) {
    params.append("debug_delay_ms", String(options.debug_delay_ms));
  }

  const url = `data/customers/${kundeIdOv}/imports/assignments/jobs/${importJobId}${params.toString() ? "?" + params.toString() : ""}`;
  try {
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error("Assignment import job status failed:", error);
    throw error;
  }
};

/**
 * Download catalogs only (contracts, objects, optional users)
 * @param {string} kundeIdOv - Customer ID
 * @param {Object} options
 * @param {('xlsx'|'zip-csv')} [options.format='xlsx'] - Output format
 * @param {boolean} [options.include_users=true] - Include users catalog
 */
export const downloadAssignmentCatalogs = async (kundeIdOv, options = {}) => {
  const params = new URLSearchParams();

  const format = options.format || "xlsx";
  params.append("format", format);

  if (options.include_users !== undefined) {
    params.append("include_users", options.include_users ? "true" : "false");
  }

  const url = `data/customers/${kundeIdOv}/imports/assignments/catalogs${params.toString() ? "?" + params.toString() : ""}`;

  try {
    const acceptHeader =
      format === "zip-csv"
        ? "application/zip"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    const response = await api.get(url, {
      responseType: "blob",
      headers: {
        Accept: acceptHeader,
      },
    });

    const blobType = acceptHeader;
    const blob = new Blob([response.data], { type: blobType });

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;

    const date = new Date().toISOString().split("T")[0];
    const ext = format === "zip-csv" ? "zip" : "xlsx";
    link.download = `${date}_kataloge_${kundeIdOv}.${ext}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return response;
  } catch (error) {
    console.error("Assignment catalogs download failed:", error);
    throw error;
  }
};

/**
 * Load paginated per-user objects for a specific user/contract/change_type
 * @param {string} kundeIdOv
 * @param {number} importJobId
 * @param {number} userId
 * @param {number} vertragId
 * @param {string} changeType - 'add' or 'remove'
 * @param {number} offset
 * @param {number} limit
 * @returns {Promise<object>} paginated objects with metadata
 */
export const loadPerUserObjects = async (
  kundeIdOv,
  importJobId,
  userId,
  vertragId,
  changeType,
  offset = 0,
  limit = 20
) => {
  const params = new URLSearchParams({
    user_id: userId,
    vertrag_id: vertragId,
    change_type: changeType,
    offset,
    limit,
  });

  const url = `data/customers/${kundeIdOv}/imports/assignments/${importJobId}/per-user-objects?${params.toString()}`;

  try {
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error("Load per-user objects failed:", error);
    throw error;
  }
};
